import { useState } from 'react';
import { Box, HStack, Button } from '@chakra-ui/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { motion } from 'framer-motion';

import { PRIMARY_BG, PRIMARY_BG_HOVER } from '../../../theme/brand';
const MotionBox = motion(Box);

const COLORS = [
    PRIMARY_BG, '#ef4444', '#10b981', '#f59e0b',
    '#8b5cf6', '#ec4899', '#14b8a6', '#f97316',
    PRIMARY_BG, '#84cc16',
];

const TAB_OPTIONS = {
    line: ['line', 'bar'],
    bar: ['line', 'bar'],
    donut: ['donut', 'bar'],
};

const TAB_LABELS = { line: 'Line', bar: 'Bar', donut: 'Donut' };

// Normalize data — handles both flat array and { type, rows } shape
const getRows = (dataValue) =>
    Array.isArray(dataValue) ? dataValue : (dataValue?.rows ?? []);

// Build Highcharts series — single or multi (when encoding.series is present)
const buildSeries = (rows, encoding) => {
    if (!encoding.series) {
        return [{
            name: encoding.y,
            data: rows.map((r) => r[encoding.y]),
            color: COLORS[0],
        }];
    }

    const xValues = [...new Set(rows.map((r) => r[encoding.x]))];
    const groups = [...new Set(rows.map((r) => r[encoding.series]))];

    return groups.map((group, i) => ({
        name: String(group),
        color: COLORS[i % COLORS.length],
        data: xValues.map((xVal) => {
            const row = rows.find(
                (r) => r[encoding.x] === xVal && r[encoding.series] === group
            );
            return row ? row[encoding.y] : null;
        }),
    }));
};

export const ChartRenderer = ({ block, data }) => {
    const raw = data?.[block.dataKey];
    const rows = getRows(raw);
    const [activeType, setActiveType] = useState(block.chartType);

    if (!rows.length) return null;

    const { encoding } = block;
    const isMultiSeries = Boolean(encoding.series);
    const tabs = TAB_OPTIONS[block.chartType] ?? ['line', 'bar'];

    const xCategories = isMultiSeries
        ? [...new Set(rows.map((r) => r[encoding.x]))]
        : rows.map((r) => r[encoding.x]);

    const getOptions = (type) => {
        if (type === 'donut') {
            const donutData = isMultiSeries
                ? [...new Set(rows.map((r) => r[encoding.series]))].map((group, i) => ({
                    name: String(group),
                    y: rows
                        .filter((r) => r[encoding.series] === group)
                        .reduce((sum, r) => sum + (r[encoding.y] ?? 0), 0),
                    color: COLORS[i % COLORS.length],
                }))
                : rows.map((r, i) => ({
                    name: r[encoding.x],
                    y: r[encoding.y],
                    color: COLORS[i % COLORS.length],
                }));

            return {
                chart: { type: 'pie', backgroundColor: 'transparent', height: 280 },
                title: { text: '' },
                tooltip: { pointFormat: '<b>{point.y:,.0f}</b> ({point.percentage:.1f}%)' },
                plotOptions: {
                    pie: {
                        innerSize: '50%',
                        allowPointSelect: true,
                        cursor: 'pointer',
                        dataLabels: { enabled: false },
                        showInLegend: true,
                    },
                },
                legend: {
                    enabled: true,
                    align: 'center',
                    verticalAlign: 'bottom',
                    layout: 'horizontal',
                    itemStyle: { fontSize: '11px', fontWeight: 'normal', color: '#4a5568' },
                },
                series: [{ name: encoding.y, colorByPoint: true, data: donutData }],
                credits: { enabled: false },
            };
        }

        return {
            chart: { type: type === 'line' ? 'line' : 'column', backgroundColor: 'transparent', height: 280 },
            title: { text: '' },
            xAxis: { categories: xCategories, crosshair: true },
            yAxis: { min: 0, title: { text: '' } },
            tooltip: { shared: true },
            plotOptions: {
                column: { borderRadius: 8, pointPadding: 0.1, groupPadding: 0.1 },
                line: { marker: { enabled: false } },
            },
            legend: {
                enabled: isMultiSeries,
                align: 'center',
                verticalAlign: 'bottom',
                layout: 'horizontal',
                itemStyle: { fontSize: '11px', fontWeight: 'normal', color: '#4a5568' },
            },
            series: buildSeries(rows, encoding),
            credits: { enabled: false },
        };
    };

    return (
        <MotionBox
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            mt={3}
            p={3}
            bg="white"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="gray.200"
        >
            <HStack gap={1} mb={3}>
                {tabs.map((type) => (
                    <Button
                        key={type}
                        size="xs"
                        onClick={() => setActiveType(type)}
                        bg={activeType === type ? PRIMARY_BG : 'white'}
                        color={activeType === type ? 'white' : 'gray.500'}
                        borderWidth="1px"
                        borderColor={activeType === type ? PRIMARY_BG : 'gray.200'}
                        _hover={{ bg: activeType === type ? PRIMARY_BG_HOVER : 'gray.50' }}
                        borderRadius="md"
                        fontWeight="medium"
                    >
                        {TAB_LABELS[type]}
                    </Button>
                ))}
            </HStack>

            <Box width="100%" overflow="hidden">
                <HighchartsReact highcharts={Highcharts} options={getOptions(activeType)} />
            </Box>
        </MotionBox>
    );
};
